import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader, Container, FormGroup, Input, Label } from 'reactstrap';
import { toast, ToastContainer } from 'react-toastify';
import { FiCamera, FiLock, FiUser } from 'react-icons/fi';
import MainLayout from '../../layouts/MainLayout';
import DefaultButton from '../../components/Buttons/Default';
import { useAuth } from '../../context/auth';
import { AWS_S3_URL } from '../../utils/constants';
import { post, postForm } from '../../utils/helpers/api';
import './styles.css';

function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [personalInfosLoading, setPersonalInfosLoading] = useState(false);
  const [securityInfosLoading, setSecurityInfosLoading] = useState(false);
  const [personalInformations, setPersonalInformations] = useState({
    firstname: user?.firstname || null,
    lastname: user?.lastname || null
  });
  const [securityInformations, setSecurityInformations] = useState({
    currentPassword: null,
    newPassword: null,
    confirmPassword: null
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarPreview(URL.createObjectURL(file));

      let formData = new FormData();
      formData.append('profile_picture', file);

      Promise.resolve(postForm('/profile/update-profile-picture', formData))
      .then(data => {
        if (data.success) {
          toast.success(t('profile.pictureUpdated'));
          localStorage.setItem('user', JSON.stringify(data.user));
        } else {
          toast.error(t('profile.pictureUpdateFailed'));
        }
      })
      .catch(() => {
        toast.error(t('profile.pictureUpdateError'));
      });
    }
  };

  const updatePersonalInfos = async () => {
    setPersonalInfosLoading(true);
    const data = await post('/profile/update-personal-infos', {
      firstname: personalInformations.firstname,
      lastname: personalInformations.lastname
    });
    if (data.success) {
      toast.success(t('profile.personalInfoUpdated'));
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    setPersonalInfosLoading(false);
  }

  const updatePassword = async () => {
    setSecurityInfosLoading(true);
    const data = await post('/profile/update-password', {
      current_password: securityInformations.currentPassword,
      new_password: securityInformations.newPassword,
      new_password_confirmation: securityInformations.confirmPassword
    });
    if (data.success) {
      toast.success(t('profile.securityInfoUpdated'));
    }
    setSecurityInfosLoading(false);
  }

  return (
    <MainLayout>
      <Container>
        <h4 className='fw-bold mb-3'>{t('profile.title')}</h4>

        <div className="profile-grid">
          {/* Personal Info Card */}
          <Card>
            <CardHeader className="d-flex align-items-center gap-2">
              <FiUser className="profile-card-icon" />
              <span className="profile-card-title">{t('profile.basicInformation')}</span>
            </CardHeader>

            <CardBody>
              <div className="profile-avatar-section">
                <div className="profile-avatar-wrapper" onClick={() => fileInputRef.current.click()}>
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt={t('common.avatar')}
                      className="profile-avatar-img border rounded-circle"
                      width={'100%'}
                      height={'100%'}
                    />
                  ) : (
                    user?.profile_picture_path ?
                      <img
                        src={`${AWS_S3_URL}${user.profile_picture_path}`}
                        alt={t('common.avatar')}
                        className="profile-avatar-img border rounded-circle"
                        width={'100%'}
                        height={'100%'}
                      />
                    :
                      <div className="profile-avatar-initials">{user?.firstname?.[0] || '?'}</div>
                  )}
                  <div className="profile-avatar-overlay">
                    <FiCamera className="profile-avatar-camera" />
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="profile-avatar-input"
                  onChange={handleAvatarChange}
                />
                <p className="profile-avatar-hint">{t('profile.clickToChangePhoto')}</p>
              </div>

              <FormGroup>
                <Label>{t('profile.firstName')}</Label>
                <Input
                  type="text"
                  placeholder={t('profile.firstName')}
                  value={personalInformations.firstname || ''}
                  onChange={(e) => setPersonalInformations({ ...personalInformations, firstname: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('profile.lastName')}</Label>
                <Input
                  type="text"
                  placeholder={t('profile.lastName')}
                  value={personalInformations.lastname || ''}
                  onChange={(e) => setPersonalInformations({ ...personalInformations, lastname: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('profile.email')}</Label>
                <Input type="email" value={user?.email ?? ''} disabled />
              </FormGroup>

              <DefaultButton
                onClick={updatePersonalInfos}
                disabled={personalInfosLoading}
                loading={personalInfosLoading}
              >
                {t('common.save')}
              </DefaultButton>
            </CardBody>
          </Card>

          {/* Change Password Card */}
          <Card>
            <CardHeader className="d-flex align-items-center gap-2">
              <FiLock className="profile-card-icon" />
              <span className="profile-card-title">{t('profile.changePassword')}</span>
            </CardHeader>

            <CardBody>
              <FormGroup>
                <Label>{t('profile.currentPassword')}</Label>
                <Input
                  type="password"
                  placeholder={t('profile.currentPasswordPlaceholder')}
                  value={securityInformations.currentPassword || ''}
                  onChange={(e) => setSecurityInformations({ ...securityInformations, currentPassword: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('profile.newPassword')}</Label>
                <Input
                  type="password"
                  placeholder={t('profile.newPasswordPlaceholder')}
                  value={securityInformations.newPassword || ''}
                  onChange={(e) => setSecurityInformations({ ...securityInformations, newPassword: e.target.value })}
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('profile.confirmNewPassword')}</Label>
                <Input
                  type="password"
                  placeholder={t('profile.confirmNewPasswordPlaceholder')}
                  value={securityInformations.confirmPassword || ''}
                  onChange={(e) => setSecurityInformations({ ...securityInformations, confirmPassword: e.target.value })}
                />
              </FormGroup>

              <DefaultButton
                onClick={updatePassword}
                disabled={securityInfosLoading}
                loading={securityInfosLoading}
              >
                {t('profile.changePassword')}
              </DefaultButton>
            </CardBody>
          </Card>
        </div>
      </Container>

      <ToastContainer
        pauseOnFocusLoss={false}
        pauseOnHover={false}
        autoClose={3000}
        position="top-center"
      />
    </MainLayout>
  );
}

export default ProfilePage;
